/**
 * Logistics Invariants
 *
 * Permission matrix mirrored from the Invoicing subdomain, adapted to the
 * logistics command taxonomy:
 *   - `user` may open/close the add modal, select a connector, submit and delete.
 *   - `api`/`url` may set the registered-integrations list (and api may drive the
 *     submit/delete success/error lifecycle + close the modal).
 *   - `ai` is read-only: it cannot submit, delete, or set list data.
 *
 * The privileged provision trigger is `submit/start`, gated by `commandOperations`
 * via the named client method, exactly as Invoicing gates `submit/start`.
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { LogisticsState } from './state';
import { LogisticsCommand } from './commands';

export const commandOperations: CommandOperationMap<LogisticsState, LogisticsCommand> = {
  'integrations/logistics/submit/start': 'ProvisionSystemIntegration',
  // No deprovision command exists yet; gate deletion under the same
  // integration-write capability so it is not silently allowed unguarded.
  'integrations/logistics/delete/start': 'ProvisionSystemIntegration'
};

export const invariants: Invariants<LogisticsState, LogisticsCommand> = {
  canApply: ({ state, command, meta }) => {
    const { type } = command;
    const { source } = meta;

    // API source — drives list/list lifecycle from policies.
    if ( source === 'api' ) {
      return (
        type === 'integrations/logistics/list/set' ||
        type === 'integrations/logistics/submit/success' ||
        type === 'integrations/logistics/submit/error' ||
        type === 'integrations/logistics/delete/success' ||
        type === 'integrations/logistics/delete/error' ||
        type === 'integrations/logistics/addModal/close'
      );
    }

    // AI source — read-only: no submit, delete, or list mutation.
    if ( source === 'ai' ) {
      if (
        type === 'integrations/logistics/submit/start' ||
        type === 'integrations/logistics/delete/start' ||
        type === 'integrations/logistics/list/set'
      ) {
        return false;
      }
      return true;
    }

    // URL source — may set the registered-integrations list.
    if ( source === 'url' ) {
      return type === 'integrations/logistics/list/set';
    }

    // User source — privileged provision trigger gated by commandOperations.
    if ( source === 'user' ) {
      if ( type === 'integrations/logistics/list/set' ) return false;
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }

    return false;
  }
};
